// The fog tool (issue 851): the DM's mode in which a drag on the board draws
// a box of squares and a click takes one, to reveal or to cover again. Held
// outside React like the pointing tool, because the canvas is driven outside
// the render loop; the canvas feeds it presses and releases, and the one
// commit per release goes to whoever listens (the board's `FogGate`, which
// acts through the table store). One board mode at a time: starting this
// stops pointing, measuring and laying an area, and each of those stops this.
import type { Cell, FogRect } from '@dragons-table/vtt-shared';
import { wallTool } from './wallTool';
import { zoneTool } from './zoneTool';
import { focusTool } from './focusTool';
import { measureTool } from './measureTool';
import { targetTool } from './targetTool';
import { areaTool } from './areaTool';
import { drawTool } from './drawTool';
import { whatsThisTool } from './whatsThisTool';

export type FogMode = 'reveal' | 'cover';
export interface FogDraft {
  on: boolean;
  mode: FogMode;
  /** The box being dragged: where the press was and where the hand is now; null between drags. */
  box: { from: Cell; to: Cell } | null;
}
export type FogCommit = { kind: 'fog.reveal' | 'fog.cover'; rect: FogRect };

/** The box of squares between two corners, in order, never below index 0. */
export function fogRect(a: Cell, b: Cell): FogRect {
  return { i0: Math.max(0, Math.min(a.i, b.i)), j0: Math.max(0, Math.min(a.j, b.j)), i1: Math.max(0, a.i, b.i), j1: Math.max(0, a.j, b.j) };
}

/** What one release sends: the mode's event over the box (a click is the one square). */
export const fogCommit = (mode: FogMode, from: Cell, to: Cell): FogCommit => ({ kind: mode === 'reveal' ? 'fog.reveal' : 'fog.cover', rect: fogRect(from, to) });

type Listener = (d: FogDraft) => void;
let draft: FogDraft = { on: false, mode: 'reveal', box: null };
const listeners = new Set<Listener>();
const committers = new Set<(c: FogCommit) => void>();
const set = (next: FogDraft) => { draft = next; for (const l of listeners) l(draft); };

export const fogTool = {
  get: () => draft,
  subscribe(listener: Listener): () => void { listeners.add(listener); return () => { listeners.delete(listener); }; },
  /** Who sends a finished box: the board's gate. */
  onCommit(fn: (c: FogCommit) => void): () => void { committers.add(fn); return () => { committers.delete(fn); }; },
  start(mode: FogMode = draft.mode): void { whatsThisTool.stop(); focusTool.stop(); measureTool.stop(); areaTool.stop(); targetTool.stop(); drawTool.stop(); wallTool.stop(); zoneTool.stop(); set({ on: true, mode, box: null }); },
  stop(): void { if (draft.on || draft.box) set({ ...draft, on: false, box: null }); },
  toggle(): void { if (draft.on) fogTool.stop(); else fogTool.start(); },
  mode(mode: FogMode): void { if (draft.on && draft.mode !== mode) set({ ...draft, mode, box: null }); },
  press(c: Cell): void { if (draft.on) set({ ...draft, box: { from: c, to: c } }); },
  drag(c: Cell): void { if (draft.on && draft.box && (draft.box.to.i !== c.i || draft.box.to.j !== c.j)) set({ ...draft, box: { from: draft.box.from, to: c } }); },
  /** A press the browser took back (`pointercancel`): the box goes, nothing is sent, the mode stays on. */
  cancel(): void { if (draft.box) set({ ...draft, box: null }); },
  /** The release: one event for the whole box, sent once; the mode stays on for the next. */
  release(c: Cell): void {
    if (!draft.on || !draft.box) return;
    const commit = fogCommit(draft.mode, draft.box.from, c);
    set({ ...draft, box: null });
    for (const fn of committers) fn(commit);
  },
};
